import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import Atc from '~/Api/Analytics/Atc';
import { ThunkDispatch } from '~/Store';
import Autocomplete, { Option } from '~/UI/Autocomplete/Autocomplete';

import { fetchAtcsByCodes, loadAtcs } from './actions';
import { selectAtcs, State as AtcsState } from './reducer';

interface State {
	medicinalBrandsAnalyticsAtcAutocomplete: AtcsState;
}

interface OwnProps {
	value?: string;
	selectedIds?: string[] | string;
	onChange?(value: string): void;
}

const mapOptions = memoizeOne((atcs: Atc[]): Option[] => {
	return atcs.map((atc) => ({
		id: `${atc.code}`,
		title: `${atc.code} ${atc.activeSubstance}`,
	}));
});

const filterOptions = memoizeOne((options: Option[], query: string): Option[] => {
	const lowerCasedQuery = query.toLowerCase();
	const filteredOptions = options.filter((option) => option.title.toLowerCase().includes(lowerCasedQuery));
	filteredOptions.sort((a, b) => a.title.localeCompare(b.title));
	return filteredOptions;
});

const mapSelectedOptions = memoizeOne((atcsByCode: { [key: string]: Atc }, selectedIds: string[]): Option[] => {
	const atcs = selectedIds.map((code) => atcsByCode[code]).filter((atc) => Boolean(atc));
	return atcs.length === selectedIds.length ? mapOptions(atcs) : [];
});

function mapStateToProps(
	{ medicinalBrandsAnalyticsAtcAutocomplete }: State,
	{ value, selectedIds }: OwnProps
): {
	options: Option[];
	selectedOptions: Option[];
} {
	const atcs = selectAtcs(medicinalBrandsAnalyticsAtcAutocomplete, value || '')?.atcs || [];
	const atcsByCode = medicinalBrandsAnalyticsAtcAutocomplete.byCode;

	const truthySelectedIds = selectedIds || [];
	const normalizedSelectedIds = Array.isArray(truthySelectedIds) ? truthySelectedIds : [truthySelectedIds];

	return {
		options: filterOptions(mapOptions(atcs), value || ''),
		selectedOptions: mapSelectedOptions(atcsByCode, normalizedSelectedIds),
	};
}

function mapDispatchToProps(
	dispatch: ThunkDispatch<AnyAction>,
	{ selectedIds, onChange }: OwnProps
): {
	onChange(value: string): void;
	onFetch(): void;
} {
	return {
		onFetch: () => {
			const truthySelectedIds = selectedIds || [];
			const normalizedSelectedIds = Array.isArray(truthySelectedIds) ? truthySelectedIds : [truthySelectedIds];

			if (normalizedSelectedIds.length > 0) {
				dispatch(fetchAtcsByCodes(normalizedSelectedIds));
			}
		},
		onChange: (value: string) => {
			onChange?.(value);
			if (value !== '') {
				dispatch(loadAtcs(value));
			}
		},
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(Autocomplete);
